import { axios } from "@cores/axiosWrapper";

/**
 * 내보내기 API가 CSV를 JSON 문자열로 인코딩해 반환하는 경우(선두/말미 큰따옴표, 이스케이프된 \r\n) 디코딩한다.
 * JSON 인코딩이 아니면(서버가 순수 CSV를 반환하도록 개선된 경우 등) 원문을 그대로 반환한다. (DAM-174)
 */
const decodeCsvExportResponse = (data) => {
    if (typeof data !== "string") return data;
    const trimmed = data.trim();
    if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
        try {
            return JSON.parse(trimmed);
        } catch {
            return data;
        }
    }
    return data;
};

export const getProjectUserAPI = (stageId, apiEndpoint, projectId, id) => {
    const headers = {
        stageId,
    };
    if (projectId) {
        headers.projectId = projectId;
    }
    return axios.get(`${apiEndpoint}/v3/managements/projects/${projectId}/members/${id}`, {
        headers,
    });
};

export const getProjectUsersAPI = (stageId, apiEndpoint, projectId, params) => {
    let queries = {
        shouldRequestDetail: params.shouldRequestDetail,
        limit: params.limit,
    };
    const headers = {
        stageId,
    };

    if (params.nextToken) queries = { ...queries, nextToken: params.nextToken };
    if (params.name) queries = { ...queries, name: params.name };
    if (params.keyword) {
        queries[params.keywordType] = params.keyword;
    }
    if (params.actionGroupIds) {
        queries.actionGroupIds = params.actionGroupIds;
    }
    if (params.siteAccess) queries.siteAccess = params.siteAccess;
    if (params.isInvitedUser) queries.isInvitedUser = params.isInvitedUser;
    if (params.isProjectAdmin) queries.isProjectAdmin = params.isProjectAdmin;
    if (params.isSpaceAdmin) queries.isSpaceAdmin = params.isSpaceAdmin;
    if (params.nameOrUsername) queries.nameOrUsername = params.nameOrUsername;

    if (projectId) {
        headers.projectId = projectId;
    }
    return axios.get(`${apiEndpoint}/v3/managements/projects/${projectId}/members`, {
        headers,
        params: queries,
    });
};

export function getUsersAPI(stageId, apiEndpoint, projectId, params) {
    let queries = {
        isDeleted: false,
        isWithdrawal: false,
        limit: params.limit,
    };
    const headers = {
        stageId,
    };

    if (params.nextToken) queries = { ...queries, nextToken: params.nextToken };
    if (params.name) queries = { ...queries, name: params.name };
    if (params.username) queries = { ...queries, username: params.username };
    if (params.keyword) {
        queries[params.keywordType] = params.keyword;
    }
    if (params.id) queries[params.id] = params.id;
    if (params.isInvitedUser) queries.isInvitedUser = params.isInvitedUser;
    if (params.isSpaceAdmin) queries.isSpaceAdmin = params.isSpaceAdmin;
    if (params.project) queries.projectId = params.project?.id;
    if (params.nameOrUsername) queries.nameOrUsername = params.nameOrUsername;
    if (params.roleId) queries.roleId = params.roleId;
    if (params.notWithRoleId) queries.notWithRoleId = params.notWithRoleId;
    if (params.hasRole) queries.hasRole = params.hasRole;

    if (projectId) {
        headers.projectId = projectId;
    }

    return axios.get(`${apiEndpoint}/v3/managements/users`, {
        params: queries,
        headers,
    });
}

// Space scope 사용자 목록 CSV 내보내기 (DAM-13). 응답 body는 CSV 문자열이라 responseType: "text".
// 단, 서버가 CSV를 JSON 문자열로 인코딩해 반환하므로 transformResponse에서 디코딩한다 (DAM-174).
export function getUsersExportAPI(stageId, apiEndpoint, params = {}) {
    let queries = {
        isDeleted: false,
        isWithdrawal: false,
    };
    const headers = {
        stageId,
    };

    if (params.name) queries.name = params.name;
    if (params.username) queries.username = params.username;
    if (params.keyword) {
        queries[params.keywordType] = params.keyword;
    }
    if (params.isInvitedUser) queries.isInvitedUser = params.isInvitedUser;
    if (params.isSpaceAdmin) queries.isSpaceAdmin = params.isSpaceAdmin;
    if (params.project) queries.projectId = params.project?.id;
    if (params.nameOrUsername) queries.nameOrUsername = params.nameOrUsername;

    return axios.get(`${apiEndpoint}/v3/managements/users/export`, {
        params: queries,
        headers,
        responseType: "text",
        transformResponse: [decodeCsvExportResponse],
    });
}

// 백엔드가 CSV를 JSON 문자열(Content-Type: application/json)로 반환하는 경우 저장 전에 디코딩한다 (DAM-175).
// 이미 순수 CSV(따옴표로 감싸이지 않음)면 원문을 그대로 반환한다.
function decodeCsvResponse(data) {
    if (typeof data !== "string") return data;
    const trimmed = data.trim();
    if (!trimmed.startsWith('"')) return data;
    try {
        return JSON.parse(trimmed);
    } catch {
        return data;
    }
}

// Project scope 멤버 목록 CSV 내보내기 (DAM-13). 서버가 CSV를 JSON 문자열로 반환하므로
// responseType: "text"로 받은 뒤 transformResponse에서 디코딩해 순수 CSV로 되돌린다 (DAM-175).
export function getProjectMembersExportAPI(stageId, apiEndpoint, projectId, params = {}) {
    let queries = {
        isDeleted: false,
        isWithdrawal: false,
    };
    const headers = {
        stageId,
    };
    if (projectId) {
        headers.projectId = projectId;
    }

    if (params.name) queries.name = params.name;
    if (params.keyword) {
        queries[params.keywordType] = params.keyword;
    }
    if (params.actionGroupIds) queries.actionGroupIds = params.actionGroupIds;
    if (params.siteAccess) queries.siteAccess = params.siteAccess;
    if (params.isInvitedUser) queries.isInvitedUser = params.isInvitedUser;
    if (params.isProjectAdmin) queries.isProjectAdmin = params.isProjectAdmin;
    if (params.isSpaceAdmin) queries.isSpaceAdmin = params.isSpaceAdmin;
    if (params.nameOrUsername) queries.nameOrUsername = params.nameOrUsername;
    // 목록 조회 thunk(getProjectUsers)와 동일하게 adminPermission을 플래그로 평탄화 (isSpaceAdmin/isProjectAdmin)
    if (params.adminPermission?.value) queries[params.adminPermission.value] = true;

    return axios.get(`${apiEndpoint}/v3/managements/projects/${projectId}/members/export`, {
        params: queries,
        headers,
        responseType: "text",
        transformResponse: [decodeCsvResponse],
    });
}

export function getUserAPI(stageId, apiEndpoint, projectId, id) {
    const headers = {
        stageId,
    };

    if (projectId) {
        headers.projectId = projectId;
    }

    return axios.get(`${apiEndpoint}/v3/managements/users/${id}`, {
        headers,
    });
}

export function getUserTeamsAPI(stageId, apiEndpoint, projectId, params) {
    const headers = {
        stageId,
    };

    if (projectId) {
        headers.projectId = projectId;
    }

    return axios.get(`${apiEndpoint}/v3/managements/teams`, {
        params,
        headers,
    });
}

export function getProjectTeamsAPI(stageId, apiEndpoint, projectId, params) {
    const headers = {
        stageId,
    };

    return axios.get(`${apiEndpoint}/v3/managements/projects/${projectId}/teams`, {
        params,
        headers,
    });
}

export function patchUserAPI(stageId, apiEndpoint, projectId, id, body) {
    const headers = {
        stageId,
    };

    if (projectId) {
        headers.projectId = projectId;
    }

    return axios.patch(`${apiEndpoint}/v3/managements/users/${id}`, body, {
        headers,
    });
}

export function deleteUserAPI(stageId, apiEndpoint, projectId, id, body) {
    const headers = {
        stageId,
    };

    if (projectId) {
        headers.projectId = projectId;
    }

    return axios.delete(`${apiEndpoint}/v3/managements/users/${id}`, {
        headers,
    });
}

export function getUserRolesAPI(stageId, apiEndpoint, projectId, params) {
    const headers = {
        stageId,
    };

    if (projectId) {
        headers.projectId = projectId;
    }

    return axios.get(`${apiEndpoint}/role-managements/roles`, {
        params,
        headers,
    });
}

export function getProjectUserRolesAPI(stageId, apiEndpoint, projectId, userId, params) {
    const headers = {
        stageId,
    };

    if (projectId) {
        headers.projectId = projectId;
    }

    return axios.get(`${apiEndpoint}/v3/managements/projects/${projectId}/members/${userId}/roles`, {
        params,
        headers,
    });
}

export function patchProjectUserAPI(stageId, apiEndpoint, projectId, userId, body) {
    const headers = {
        stageId,
        projectId,
    };

    return axios.patch(`${apiEndpoint}/v3/managements/projects/${projectId}/members/${userId}`, body, {
        headers,
    });
}

export function inviteSpacePendingUserAPI(stageId, apiEndpoint, body) {
    const headers = { stageId };
    return axios.post(`${apiEndpoint}/user-invitation/space-pending-users/actions`, body, {
        headers,
    });
}

export function inviteProjectPendingUserAPI(stageId, apiEndpoint, projectId, body) {
    const headers = { stageId, projectId };
    return axios.post(`${apiEndpoint}/user-invitation/project-pending-users/actions`, body, {
        headers,
    });
}

export function getSpacePendingUsersAPI(stageId, apiEndpoint, params) {
    const headers = { stageId };
    return axios.get(`${apiEndpoint}/user-invitation/space-pending-users`, {
        params,
        headers,
    });
}

export function getProjectPendingUsersAPI(stageId, apiEndpoint, projectId, params) {
    const headers = { stageId, projectId };
    return axios.get(`${apiEndpoint}/user-invitation/project-pending-users`, {
        params,
        headers,
    });
}

export function getProjectUserTeamsAPI(stageId, apiEndpoint, projectId, userId, params) {
    const headers = {
        stageId,
        projectId,
    };

    return axios.get(`${apiEndpoint}/v3/managements/projects/${projectId}/members/${userId}/teams`, {
        params,
        headers,
    });
}
